import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayIcon from "@mui/icons-material/Replay";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { getApiErrorStatus, getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import { HttpStatusCode } from "axios";
import { useGetOnboardingWorkTemplateCatalogQuery } from "legal-entities/agreements/_api/entity-agreement-api-service";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useState } from "react";
import type { UserOnboardingWork } from "users/information/user-onboarding-work";
import {
  SuperUserAPIError,
  useCloseOnboardingMutation,
  useReplaceOnboardingProposalMutation,
  useRetryOnboardingProvisioningMutation,
} from "users/super-users/_api/super-user-api";

const provisioningReasonDescriptions: Readonly<Record<string, string>> = {
  retry_age_exceeded: "it kept failing for longer than its retry window",
  retry_limit_reached: "it ran out of retry attempts",
  invalid_request: "its stored request was invalid",
};

// The super-user endpoints reject with a SuperUserAPIError, which keeps the RTK error under
// `.error`. The status and the API's own message are read from there (#6493).
function toApiResponse(error: unknown): unknown {
  return error instanceof SuperUserAPIError ? error.error : error;
}

type OnboardingWorkResolutionProps = {
  onboardingWork: UserOnboardingWork;
  onChanged: () => void;
};

export default function OnboardingWorkResolution({ onboardingWork, onChanged }: Readonly<OnboardingWorkResolutionProps>) {
  const templateCatalogQuery = useGetOnboardingWorkTemplateCatalogQuery();
  const { data: templateData } = templateCatalogQuery;
  const templates = templateData ?? [];
  const [replaceProposal, replaceState] = useReplaceOnboardingProposalMutation();
  const [closeOnboarding, closeState] = useCloseOnboardingMutation();
  const [retryProvisioning, retryState] = useRetryOnboardingProvisioningMutation();
  const [templateId, setTemplateId] = useState("");
  const [unavailableTemplateId, setUnavailableTemplateId] = useState<string | undefined>();
  const [reason, setReason] = useState("");

  const replacementTemplates = templates.filter((template) => template.workflowVersion >= 2);
  const selectableReplacementTemplates = replacementTemplates.filter(
    (template) => template.id !== unavailableTemplateId,
  );
  const selectedTemplateIsUnavailable = unavailableTemplateId === templateId && Boolean(templateId);
  const sourceTemplateUnavailable = Boolean(
    templateData &&
      !replacementTemplates.some((template) => template.id === onboardingWork.templateId),
  );

  const isClosed = onboardingWork.state === "Closed";
  const isCompleted = onboardingWork.state === "Completed";
  const needsReplacement = onboardingWork.state === "StaffResolutionRequired";
  const canReplace =
    needsReplacement && Boolean(templateId) && !selectedTemplateIsUnavailable;
  const canClose = !isClosed && !isCompleted;
  // Only a request the provisioning worker parked can go back to its queue. One its negotiation
  // parked has already sent its proposal, so retrying would have nothing to do.
  const provisioningReason = needsReplacement ? onboardingWork.provisioningStaffResolutionReason : undefined;

  async function handleReplace() {
    if (!canReplace) return;
    try {
      await replaceProposal({ requestId: onboardingWork.requestId, templateId }).unwrap();
      enqueueSnackbar("Replacement proposal created", { variant: "success" });
      onChanged();
    } catch (error) {
      const response = toApiResponse(error);
      const status = getApiErrorStatus(response);
      if (status === HttpStatusCode.NotFound || status === HttpStatusCode.Conflict) {
        const refreshed = await templateCatalogQuery.refetch();
        const currentReplacementTemplates = (refreshed.data ?? []).filter((template) => template.workflowVersion >= 2);
        if (!currentReplacementTemplates.some((template) => template.id === templateId)) {
          setUnavailableTemplateId(templateId);
          return;
        }
      }
      enqueueSnackbar(getErrorMessageFromUnknown(response) ?? "Could not create replacement proposal", { variant: "error" });
    }
  }

  async function handleRetry() {
    try {
      await retryProvisioning({ requestId: onboardingWork.requestId }).unwrap();
      enqueueSnackbar("Onboarding returned to the provisioning queue", { variant: "success" });
      onChanged();
    } catch (error) {
      enqueueSnackbar(getErrorMessageFromUnknown(toApiResponse(error)) ?? "Could not retry onboarding", { variant: "error" });
    }
  }

  async function handleClose() {
    if (!reason.trim()) return;
    try {
      await closeOnboarding({ requestId: onboardingWork.requestId, reason: reason.trim() }).unwrap();
      enqueueSnackbar("Onboarding closed", { variant: "success" });
      setReason("");
      onChanged();
    } catch (error) {
      enqueueSnackbar(getErrorMessageFromUnknown(toApiResponse(error)) ?? "Could not close onboarding", { variant: "error" });
    }
  }

  return (
    <Stack spacing={0.75} sx={{ minWidth: 280, py: 0.5 }}>
      <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
        <Chip
          size="small"
          color={isClosed ? "default" : isCompleted ? "success" : onboardingWork.state === "StaffResolutionRequired" ? "warning" : "info"}
          label={onboardingWork.state}
          icon={isCompleted ? <CheckCircleOutlinedIcon /> : isClosed ? <CloseIcon /> : undefined}
        />
        <Typography variant="caption">Template {onboardingWork.templateId} v{onboardingWork.templateVersion}</Typography>
      </Stack>
      <Typography variant="caption">Current proposal: {onboardingWork.currentProposalId ?? "—"}</Typography>
      <Typography variant="caption" sx={{ overflowWrap: "anywhere" }}>
        Proposal history ({onboardingWork.relatedProposalIds.length}): {onboardingWork.relatedProposalIds.join(", ") || "—"}
      </Typography>
      {onboardingWork.state === "Closed" && (
        <Typography variant="caption" color="text.secondary">
          Closure reason: {onboardingWork.closureReason ?? "Not recorded"}
        </Typography>
      )}
      {provisioningReason && (
        <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
          <Typography variant="caption" color="text.secondary">
            Provisioning stopped because {provisioningReasonDescriptions[provisioningReason] ?? provisioningReason}.
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            disabled={retryState.isLoading}
            onClick={() => void handleRetry()}
          >
            Retry
          </Button>
        </Stack>
      )}
      {needsReplacement && (
        <>
        {sourceTemplateUnavailable && (
          <Alert severity="warning" role="status">
            This onboarding template is no longer available. Choose a current template and try again.
          </Alert>
        )}
        {selectedTemplateIsUnavailable && (
          <Alert severity="warning" role="alert">
            This onboarding template is no longer available. Choose a current template and try again.
          </Alert>
        )}
        <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
          <FormControl size="small" sx={{ minWidth: 165 }}>
            <InputLabel id={`replacement-template-${onboardingWork.requestId}`}>Replacement template</InputLabel>
            <Select
              labelId={`replacement-template-${onboardingWork.requestId}`}
              value={templateId}
              label="Replacement template"
              onChange={(event) => {
                setTemplateId(event.target.value);
                setUnavailableTemplateId(undefined);
              }}
            >
              <MenuItem value="" disabled>
                Choose a template
              </MenuItem>
              {selectedTemplateIsUnavailable && (
                <MenuItem value={templateId} disabled>
                  {templateId} (unavailable)
                </MenuItem>
              )}
              {selectableReplacementTemplates.map((template) => (
                <MenuItem key={`${template.id}:${template.version}`} value={template.id}>
                  {template.displayName} ({template.version})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ReplayIcon />}
            disabled={!canReplace || replaceState.isLoading}
            onClick={() => void handleReplace()}
          >
            Replace
          </Button>
        </Stack>
        </>
      )}
      {canClose && (
        <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
          <TextField
            size="small"
            label="Closure reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            slotProps={{ input: { "aria-label": `Closure reason for ${onboardingWork.requestId}` } }}
          />
          <Button
            size="small"
            color="error"
            variant="outlined"
            startIcon={<CloseIcon />}
            disabled={!reason.trim() || closeState.isLoading}
            onClick={() => void handleClose()}
          >
            Close
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
